import {
  useConnection,
  useConnections,
  useIntegrationApp,
  useIntegrations,
} from '@membranehq/react';
import type { Integration } from '@membranehq/sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotification } from 'src/contexts/NotificationContext';
import { useIntegrationFields } from 'src/hooks/useIntegrationFields';
import { useIntegrationForm } from 'src/hooks/useIntegrationForm';
import { useOAuthConnection } from 'src/hooks/useOAuthConnection';
import {
  getFilteredFieldsForOption,
  getSelectableAuthOptions,
  hasRealInputFieldsForOption,
  isOAuth,
  isOAuthOrProxyOption,
  OAuthStatus,
} from 'src/utils/integrationUtils';
import { Button } from './Button';
import { Label } from './Label';
import { SelectBox } from './SelectBox';
import { ConnectionFormFields } from './IntegrationAppConnectionModal/ConnectionFormFields';
import { ConnectionStatusView } from './IntegrationAppConnectionModal/ConnectionStatusView';
import { DeleteConnectionModal } from './IntegrationAppConnectionModal/DeleteConnectionModal';
import { OAuthStatusView } from './IntegrationAppConnectionModal/OAuthStatusView';
import { Loading } from './Loading';
import { Modal } from './Modal';

type IntegrationAppConnectionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  integrationKey?: string;
  onSuccess?: () => void;
};

export const IntegrationAppConnectionModal = ({
  isOpen,
  onClose,
  integrationKey,
  onSuccess,
}: IntegrationAppConnectionModalProps) => {
  const integrationApp = useIntegrationApp();
  const { refresh: refreshIntegrations } = useIntegrations();
  const { refresh: refreshConnections } = useConnections();
  const notification = useNotification();
  const [loading, setLoading] = useState(false);
  const [integration, setIntegration] = useState<Integration | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showReconnectForm, setShowReconnectForm] = useState(false);
  const [hasOpenedPopup, setHasOpenedPopup] = useState(false);
  const [selectedAuthOptionKey, setSelectedAuthOptionKey] = useState<
    string | undefined
  >(undefined);
  const { connection: existingConnection } = useConnection(integrationKey);

  const {
    formData,
    errors,
    initializeFormData,
    handleInputChange,
    getSchema,
    validateForm,
    resetForm,
  } = useIntegrationForm(integration, selectedAuthOptionKey);

  const handleConnectionSuccess = useCallback(
    async (message: string, shouldResetForm = false) => {
      onClose();
      await refreshIntegrations();
      await refreshConnections();
      notification.success(message);

      onSuccess?.();
      if (shouldResetForm) {
        resetForm();
        setShowReconnectForm(false);
      }
    },
    [
      notification,
      refreshIntegrations,
      refreshConnections,
      onSuccess,
      onClose,
      resetForm,
    ],
  );

  const {
    oauthStatus,
    setOauthStatus,
    handleOAuthConnect,
    handleRefresh: handleOAuthRefresh,
    lastSuccessMessage,
  } = useOAuthConnection({
    integrationKey,
    integration,
    integrationApp,
  });

  const { getFieldLabel, getFieldType, getFieldDescription, isFieldRequired } =
    useIntegrationFields({
      integration,
      getSchema,
    });

  useEffect(() => {
    if (!isOpen || !integrationKey || !integrationApp) {
      setShowReconnectForm(false);
      setOauthStatus(OAuthStatus.Idle);
      setIntegration(null);
      setHasOpenedPopup(false);
      setSelectedAuthOptionKey(undefined);
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, integrationKey, integrationApp, resetForm]);

  useEffect(() => {
    if (oauthStatus !== OAuthStatus.Success || !lastSuccessMessage) return;

    const message = lastSuccessMessage;
    setOauthStatus(OAuthStatus.Idle);
    const runSuccessSideEffects = async () => {
      onClose();
      await refreshIntegrations();
      await refreshConnections();
      notification.success(message);
      onSuccess?.();
      resetForm();
      setShowReconnectForm(false);
    };

    void runSuccessSideEffects();
  }, [
    oauthStatus,
    lastSuccessMessage,
    onClose,
    refreshIntegrations,
    refreshConnections,
    notification,
    onSuccess,
    resetForm,
    setOauthStatus,
  ]);

  useEffect(() => {
    if (!isOpen || !integrationKey || !integrationApp) return;

    let stale = false;
    const loadIntegration = async () => {
      try {
        setLoading(true);
        const keyAtStart = integrationKey;
        const integration = await integrationApp
          .integration(integrationKey)
          .get();

        if (stale || keyAtStart !== integrationKey) return;
        setIntegration(integration);
        initializeFormData(integration);
        const options = getSelectableAuthOptions(integration);
        setSelectedAuthOptionKey(options[0]?.key);
        setLoading(false);
      } catch (error) {
        if (stale) return;
        console.error('Error loading integration:', error);
        notification.error('Failed to load integration details');
        onClose();
        setLoading(false);
      }
    };

    loadIntegration();
    return () => {
      stale = true;
    };
  }, [
    isOpen,
    integrationKey,
    integrationApp,
    initializeFormData,
    notification,
    onClose,
  ]);

  const selectedOption = integration?.authOptions?.find(
    (opt) => opt.key === selectedAuthOptionKey,
  );
  const integrationIsOAuthSelected =
    selectedOption !== undefined
      ? isOAuthOrProxyOption(selectedOption)
      : isOAuth(integration);

  const handleReconnect = useCallback(async () => {
    if (!integrationApp || !integrationKey || !integration) return;

    if (integrationIsOAuthSelected) {
      if (hasRealInputFieldsForOption(integration, selectedAuthOptionKey)) {
        setShowReconnectForm(true);
        return;
      }
      setLoading(true);
      setHasOpenedPopup(true);
      await handleOAuthConnect({
        input: formData,
        parameters: formData,
        authOptionKey: selectedAuthOptionKey,
      });
      setLoading(false);
      return;
    }

    if (hasRealInputFieldsForOption(integration, selectedAuthOptionKey)) {
      setShowReconnectForm(true);
      return;
    }

    try {
      setLoading(true);
      await integrationApp.integration(integrationKey).connect({
        input: formData,
        parameters: formData,
        ...(selectedAuthOptionKey && { authOptionKey: selectedAuthOptionKey }),
      });

      await handleConnectionSuccess('Connection reconnected successfully');
      setShowReconnectForm(false);
    } catch (error: unknown) {
      console.error('Error reconnecting:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to reconnect';
      notification.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [
    integrationApp,
    integrationKey,
    integration,
    formData,
    selectedAuthOptionKey,
    integrationIsOAuthSelected,
    handleOAuthConnect,
    handleConnectionSuccess,
    notification,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!integrationApp || !integrationKey) return;

    if (integrationIsOAuthSelected) {
      if (
        hasRealInputFieldsForOption(integration, selectedAuthOptionKey) &&
        !validateForm()
      ) {
        notification.error('Please fill in all required fields');
        return;
      }
      setHasOpenedPopup(true);
      setLoading(true);
      await handleOAuthConnect({
        input: formData,
        parameters: formData,
        authOptionKey: selectedAuthOptionKey,
      });
      setLoading(false);
      return;
    }

    if (!validateForm()) {
      notification.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      await integrationApp.integration(integrationKey).connect({
        input: formData,
        parameters: formData,
        ...(selectedAuthOptionKey && { authOptionKey: selectedAuthOptionKey }),
      });

      await handleConnectionSuccess(
        existingConnection
          ? 'Connection reconnected successfully'
          : 'Connection created successfully',
        true,
      );
    } catch (error: unknown) {
      console.error('Error creating connection:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create connection';
      notification.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [
    integrationApp,
    integrationKey,
    integration,
    formData,
    selectedAuthOptionKey,
    integrationIsOAuthSelected,
    validateForm,
    handleOAuthConnect,
    handleConnectionSuccess,
    existingConnection,
    notification,
  ]);

  const handleDelete = useCallback(async () => {
    if (!integrationApp || !integrationKey) return;
    if (!existingConnection?.id) {
      notification.error('Connection no longer exists.');
      return;
    }

    try {
      setDeleting(true);
      await integrationApp.connection(existingConnection.id).archive();
      await refreshIntegrations();
      await refreshConnections();

      notification.success('Connection deleted successfully');
      onSuccess?.();
      setDeleteModalOpen(false);
      onClose();
    } catch (error: unknown) {
      console.error('Error deleting connection:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete connection';
      notification.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  }, [
    integrationApp,
    integrationKey,
    notification,
    refreshIntegrations,
    refreshConnections,
    onSuccess,
    onClose,
    existingConnection,
  ]);

  const handleReopenConnectionWindow = useCallback(async () => {
    setLoading(true);
    await handleOAuthConnect({
      input: formData,
      parameters: formData,
      authOptionKey: selectedAuthOptionKey,
    });
    setLoading(false);
  }, [handleOAuthConnect, formData, selectedAuthOptionKey]);

  const authOptions = getSelectableAuthOptions(integration);
  const fields = useMemo(() => {
    if (!integration) return [];
    return getFilteredFieldsForOption(integration, selectedAuthOptionKey).map(
      (field) => ({
        key: field,
        label: getFieldLabel(field),
        type: getFieldType(field),
        description: getFieldDescription(field),
        required: isFieldRequired(field),
      }),
    );
  }, [
    integration,
    selectedAuthOptionKey,
    getFieldLabel,
    getFieldType,
    getFieldDescription,
    isFieldRequired,
  ]);

  if (!integrationKey) {
    return null;
  }

  const renderActionButton = () => {
    if (
      oauthStatus === OAuthStatus.Waiting ||
      oauthStatus === OAuthStatus.Closed
    ) {
      return (
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} variant="outlined">
            {oauthStatus === OAuthStatus.Waiting ? 'Cancel' : 'Close'}
          </Button>
          {hasOpenedPopup && (
            <Button onClick={handleReopenConnectionWindow}>
              Reopen Connection Window
            </Button>
          )}
          <Button onClick={handleOAuthRefresh}>
            <span className="flex items-center gap-2">
              {oauthStatus === OAuthStatus.Waiting && <Loading size="4" />}
              <span>Refresh</span>
            </span>
          </Button>
        </div>
      );
    }

    if (oauthStatus === OAuthStatus.Success) {
      return (
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      );
    }

    if (
      (!existingConnection || showReconnectForm) &&
      (oauthStatus === OAuthStatus.Idle || !loading)
    ) {
      return (
        <div className="flex justify-end gap-2">
          <Button
            variant="outlined"
            onClick={() => {
              if (showReconnectForm) {
                setShowReconnectForm(false);
                resetForm();
              } else {
                onClose();
              }
            }}
            disabled={loading || deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || deleting || !integration}
          >
            {loading
              ? existingConnection
                ? 'Reconnecting...'
                : 'Connecting...'
              : existingConnection
                ? 'Reconnect'
                : 'Connect'}
          </Button>
        </div>
      );
    }

    return (
      <Button onClick={onClose} variant="outlined">
        Close
      </Button>
    );
  };

  const renderContent = () => {
    if (loading && !integration && oauthStatus === OAuthStatus.Idle) {
      return (
        <div className="flex gap-2 items-center justify-center py-12">
          <Loading size="8" />
          <span className="text-neutral-400">
            Loading integration details...
          </span>
        </div>
      );
    }

    if (
      oauthStatus === OAuthStatus.Waiting ||
      oauthStatus === OAuthStatus.Closed ||
      oauthStatus === OAuthStatus.Success
    ) {
      return (
        <OAuthStatusView
          status={oauthStatus}
          existingConnection={!!existingConnection}
        />
      );
    }

    if (existingConnection && !showReconnectForm) {
      return (
        <ConnectionStatusView
          onReconnect={handleReconnect}
          onDelete={() => setDeleteModalOpen(true)}
          loading={loading}
          deleting={deleting}
        />
      );
    }

    const hasOAuthInputFields = hasRealInputFieldsForOption(
      integration,
      selectedAuthOptionKey,
    );
    const shouldShowFormFields =
      fields.length > 0 && (!integrationIsOAuthSelected || hasOAuthInputFields);

    return (
      <>
        {authOptions.length > 1 && (
          <div className="space-y-2">
            <Label htmlFor="integration-auth-option">
              Authentication method
            </Label>
            <SelectBox
              id="integration-auth-option"
              options={authOptions.map((opt) => ({
                value: opt.key,
                label: opt.label,
              }))}
              value={
                selectedAuthOptionKey
                  ? {
                      value: selectedAuthOptionKey,
                      label:
                        authOptions.find((o) => o.key === selectedAuthOptionKey)
                          ?.label ?? selectedAuthOptionKey,
                    }
                  : null
              }
              onChange={(value) => {
                setSelectedAuthOptionKey(value || undefined);
                resetForm();
              }}
              placeholder="Select authentication method..."
            />
          </div>
        )}
        {shouldShowFormFields ? (
          <ConnectionFormFields
            fields={fields}
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
          />
        ) : (
          <div className="py-4">
            <span className="text-neutral-600">
              {integrationIsOAuthSelected
                ? 'Click Connect to authenticate via OAuth.'
                : 'No additional configuration required. Click Connect to proceed.'}
            </span>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          loading && oauthStatus === OAuthStatus.Idle
            ? 'Loading...'
            : `Connect ${integration?.name || integrationKey}`
        }
        className="lg:max-w-[50%]"
        showClose={false}
        actionButton={renderActionButton()}
      >
        <div className="space-y-4">{renderContent()}</div>
      </Modal>

      <DeleteConnectionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={handleDelete}
        deleting={deleting}
      />
    </>
  );
};
